import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin } from "lucide-react";
import { CONTACT } from "@/content/policies";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/safety", label: "Safety" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-cream/85 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-[72px] md:px-8">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <img src="/middleman-icon.png" alt="Middle Man logo" className="h-9 w-9 rounded-full border border-line object-cover md:h-10 md:w-10" />
          <span className="font-serif text-lg font-bold tracking-[0.18em] text-ink md:text-xl">MIDDLE MAN</span>
        </Link>
        <nav className="hidden items-center gap-7 lg:flex" data-testid="header-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-brand" : "text-mute hover:text-ink"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/download"
            data-testid="header-get-app-btn"
            className="hidden rounded-full bg-ink px-5 py-2.5 text-[13px] font-bold text-cream transition-colors duration-300 hover:bg-brand sm:inline-block"
          >
            Get the App
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center border border-line text-ink lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-line bg-cream px-6 py-6 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {[...NAV, { to: "/join", label: "Join as Middle Man" }, { to: "/download", label: "Download App" }].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`font-serif text-2xl font-semibold ${pathname === n.to ? "text-brand" : "text-ink"}`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

const FOOT_COLS = [
  {
    title: "Platform",
    links: [
      { to: "/services", label: "Services" },
      { to: "/pricing", label: "Recommended Charges" },
      { to: "/how-it-works", label: "How It Works" },
      { to: "/for-customers", label: "For Customers" },
      { to: "/join", label: "Join as Middle Man" },
      { to: "/download", label: "Download App" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About Us" },
      { to: "/safety", label: "Safety" },
      { to: "/contact", label: "Contact" },
      { to: "/grievance", label: "Grievance Support" },
      { to: "/delete-account", label: "Delete Account" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/refund", label: "Refund & Cancellation" },
      { to: "/product-order-terms", label: "Product Order Terms" },
    ],
  },
  {
    title: "Policies",
    links: [
      { to: "/marketplace-policy", label: "Marketplace Policy" },
      { to: "/seller-policy", label: "Seller Policy" },
      { to: "/healthcare-notice", label: "Healthcare Notice" },
    ],
  },
];

export function Footer() {
  return (
    <footer className="relative overflow-hidden bg-ink text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 pb-10 pt-16 md:pt-24">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <img src="/middleman-icon.png" alt="Middle Man logo" className="h-11 w-11 rounded-full border border-cream/20 object-cover" />
              <span className="font-serif text-xl font-bold tracking-[0.18em]">MIDDLE MAN</span>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-cream/60">
              Verified local help for everyday tasks — errands, deliveries, elderly care, government work and a local
              product marketplace, from nearby verified Middle Men.
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-cream/70">
              <a data-testid="footer-email" href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 transition-colors hover:text-brand">
                <Mail className="h-4 w-4 text-brand" /> {CONTACT.email}
              </a>
              <a data-testid="footer-phone" href={CONTACT.phoneHref} className="flex items-center gap-2.5 transition-colors hover:text-brand">
                <Phone className="h-4 w-4 text-brand" /> {CONTACT.phone}
              </a>
              <p className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <span>Narhe (N.V.), Pune 411041, Maharashtra, India</span>
              </p>
            </div>
          </div>
          {FOOT_COLS.map((col) => (
            <div key={col.title}>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand">{col.title}</p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-cream/65 transition-colors duration-200 hover:text-cream">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 select-none overflow-hidden" aria-hidden="true">
          <p className="whitespace-nowrap text-center font-serif text-[13.5vw] font-bold leading-none tracking-tight text-transparent" style={{ WebkitTextStroke: "1px rgba(249,246,240,0.22)" }}>
            MIDDLE MAN
          </p>
        </div>

        <div className="mt-10 border-t border-cream/10 pt-8 text-[13px] leading-relaxed text-cream/50">
          <p data-testid="footer-operator-line" className="font-semibold text-cream/75">
            Middle Man is a brand operated by Tourist Junction Private Limited.
          </p>
          <p className="mt-2">Registered Office: {CONTACT.address}</p>
          <p className="mt-3">© {new Date().getFullYear()} Tourist Junction Private Limited. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
